import { useState, useEffect, useRef, useCallback } from 'react';

export default function useScrollSpy(sectionIds, options = {}) {
  const [activeId, setActiveId] = useState(sectionIds[0] || '');
  const observerRef = useRef(null);
  const manualOverrideRef = useRef(null);

  // Allow external callers (e.g. SideNav click) to force the active section.
  // The override expires after 1.2s so normal scroll detection resumes.
  const forceActive = useCallback((id) => {
    setActiveId(id);
    manualOverrideRef.current = id;
    setTimeout(() => { manualOverrideRef.current = null; }, 1200);
  }, []);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    const entries = new Map();

    observerRef.current = new IntersectionObserver((observedEntries) => {
      if (manualOverrideRef.current) return; // skip while override active

      observedEntries.forEach(entry => {
        entries.set(entry.target.id, entry);
      });

      // Find the bottommost section whose top is above viewport center.
      // This works better for the last section than "first intersecting".
      let best = null;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.4) {
          best = id;
        }
      }
      if (best) setActiveId(best);
    }, {
      rootMargin: options.rootMargin || '-80px 0px -40% 0px',
      threshold: options.threshold || [0, 0.1, 0.25, 0.5],
    });

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    // When near the bottom of the page, snap to the last section
    const handleScroll = () => {
      if (manualOverrideRef.current) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 150) {
        const last = sectionIds[sectionIds.length - 1];
        if (last) setActiveId(last);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sectionIds]);

  return [activeId, forceActive];
}
